import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Divider, Stack, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Badge,
  CustomButton,
  TextField,
  CustomSelect,
  DatePicker,
  CustomModal,
  CustomSnackbar,
  StatusChip,
  CustomTab,
  BentoBoxGrid,
  BentoItem,
  CustomCheckbox,
  CustomRadioGroup,
  CustomRadio,
  ToggleSwitch,
  StandardChip,
  CustomTooltip
} from './index';
import { 
  MenuItem, 
  Tabs,
  Card,
  CardContent,
  Grid,
  Paper
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person,
  Close
} from '@mui/icons-material';
import StepperShowcase from './Stepper/StepperShowcase';
import PaperElevation from './PaperElevation';
import Avatar from './Avatar';
import IllustrationWrapper from '../../v3/Illustrration/IllustrationWrapper';
import NoInventorySvg from '../../v3/Illustrration/components/NoInventorySvg';
import EmptyCartSvg from '../../v3/Illustrration/components/EmptyCartSvg';
import DeliveryTruckSvg from '../../v3/Illustrration/components/DeliveryTruckSvg';
import NoDataFoundSvg from '../../v3/Illustrration/components/NoDataFoundSvg';

const ComponentShowcase = () => {
  // State for interactive components
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectValue, setSelectValue] = useState('');
  const [textFieldValue, setTextFieldValue] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateError, setDateError] = useState(null);

  // Checkbox and Radio states
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(true);
  const [radioValue, setRadioValue] = useState('option1');
  const [planValue, setPlanValue] = useState('basic');
  const [orientationValue, setOrientationValue] = useState('horizontal');

  // Toggle Switch states
  const [toggleChecked, setToggleChecked] = useState(false);
  const [notificationToggle, setNotificationToggle] = useState(true);
  const [darkModeToggle, setDarkModeToggle] = useState(false);
  const [privacyToggle, setPrivacyToggle] = useState(true);

  const planOptions = [
    { value: 'basic', label: 'Basic Plan ($9/month)' },
    { value: 'premium', label: 'Premium Plan ($19/month)' },
    { value: 'enterprise', label: 'Enterprise Plan ($49/month)' },
    { value: 'custom', label: 'Custom Plan', disabled: true }
  ];
  // Checkbox and Radio states ends


  const StatusEnums = {
    Pending: 'pending',
    NotVerified: 'not-verified',
    Verified: 'Verified',
    Approved: 'approved',
    PartiallyCompleted: 'partially-completed',
    Completed: 'completed',
    Success: 'success',
    Cancelled: 'cancelled'
  };
  const menuItems = [
    { label: 'Profile', onClick: () => alert('Profile') },
    { label: 'Logout', onClick: () => alert('Logout') },
  ];

  // Chip State
  const [chipsData, setChipsData] = useState([]);
  const [multiSelected, setMultiSelected] = useState([]);
  const [singleSelected, setSingleSelected] = useState(null);

  useEffect(() => {
    // Fake API call
    setTimeout(() => {
      setChipsData([
        { id: 1, name: 'Health Insurance' },
        { id: 2, name: 'Retirement Plan' },
        { id: 3, name: 'Life Cover' },
      ]);
    }, 300);
  }, []);

  const toggleMulti = (id, isSelected) => {
    setMultiSelected(prev =>
      isSelected ? [...prev, id] : prev.filter(x => x !== id)
    );
  };



  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Humbee Design System Components
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Showcase of all MUI custom components with examples and use cases
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Basic UI Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Basic UI Components
        </Typography>
        

        {/* Badge Examples */}
        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Badge Component
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'relative', height: 120 }}>
                <Badge 
                  badgeText="New" 
                  badgeType="card" 
                  badgePosition={{top: 8, right: 8}}
                  badgeSize="small"
                />
                <CardContent>
                  <Typography>Product Card with New Badge</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used for highlighting new products or features
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'relative', height: 120 }}>
                <Badge 
                  badgeText="Sale" 
                  badgeType="list" 
                  badgePosition={{top: 0, right: 0}}
                  badgeSize="small"
                />
                <CardContent sx={{ pt: 3 }}>
                  <Typography>List Item with Sale Badge</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used for promotional items in lists
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'relative', height: 120 }}>
                <Badge 
                  badgeText="Limited" 
                  badgeType="default" 
                  badgePosition={{top: 8, right: 8}}
                  badgeSize="large"
                />
                <CardContent>
                  <Typography>Default Badge Style</Typography>
                  <Typography variant="body2" color="text.secondary">
                    General purpose badge for any content
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Button Examples */}
        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Custom Button Component
          </Typography>
          
          {/* Button Variants */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Button Variants</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <CustomButton
                variant="filled"
                color="primary"
                label="Primary Filled"
                leftIcon={ShoppingCartIcon}
              />
              <CustomButton
                variant="outlined"
                color="primary"
                label="Primary Outlined"
                rightIcon={AddIcon}
              />
              <CustomButton
                variant="text"
                color="primary"
                label="Primary Text"
              />
            </Stack>
          </Box>

          {/* Button Colors */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Button Colors</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <CustomButton
                variant="filled"
                color="primary"
                label="Primary"
                leftIcon={SaveIcon}
              />
              <CustomButton
                variant="filled"
                color="secondary"
                label="Secondary"
                leftIcon={EditIcon}
              />
              <CustomButton
                variant="filled"
                color="success"
                label="Success"
                leftIcon={SaveIcon}
              />
              <CustomButton
                variant="filled"
                color="error"
                label="Error"
                leftIcon={DeleteIcon}
              />
              <CustomButton
                variant="filled"
                color="neutral"
                label="Cancel"
                leftIcon={CancelIcon}
              />
            </Stack>
          </Box>

          {/* Button States */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Button States</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <CustomButton
                variant="filled"
                color="primary"
                label="Normal State"
              />
              <CustomButton
                variant="filled"
                color="primary"
                label="Disabled State"
                disabled
              />
            </Stack>
          </Box>
        </Box>

        {/* TextField Examples */}
        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Text Field Component
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email Address"
                variant="outlined"
                placeholder="Enter your email"
                value={textFieldValue}
                onChange={(e) => setTextFieldValue(e.target.value)}
                helperText="We'll never share your email"
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                variant="outlined"
                placeholder="Enter description"
                multiline
                rows={4}
                helperText="Maximum 500 characters"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Standard Variant"
                variant="standard"
                placeholder="Standard input"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Error State"
                variant="outlined"
                placeholder="Enter value"
                error
                helperText="This field is required"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Disabled State"
                variant="outlined"
                placeholder="This field is disabled"
                disabled
                helperText="Cannot interact with this field"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Form Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Form Components
        </Typography>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Custom Select Component
          </Typography>
          <Box maxWidth={300}>
            <CustomSelect
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Category</em>
              </MenuItem>
              <MenuItem value="electronics">Electronics</MenuItem>
              <MenuItem value="clothing">Clothing</MenuItem>
              <MenuItem value="books">Books</MenuItem>
              <MenuItem value="home">Home & Garden</MenuItem>
            </CustomSelect>
          </Box>
        </Box>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Date Picker Component
          </Typography>
          <Typography variant="body1" paragraph>
            Customized date picker with Humbee styling and validation
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Event Date"
                  variant="outlined"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  onError={(error) => setDateError(error)}
                  helperText={dateError || "Select an event date"}
                  error={!!dateError}
                  placeholder="DD/MM/YYYY"
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Birth Date"
                  variant="outlined"
                  value={null}
                  onChange={() => {}}
                  disableFuture
                  helperText="Future dates are disabled"
                  placeholder="DD/MM/YYYY"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Appointment Date"
                  variant="standard"
                  value={null}
                  onChange={() => {}}
                  disablePast
                  helperText="Past dates are disabled"
                  placeholder="Select appointment date"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Date Range Start"
                  variant="outlined"
                  value={dayjs()}
                  onChange={() => {}}
                  minDate={dayjs()}
                  maxDate={dayjs().add(1, 'year')}
                  helperText="Within the next year only"
                  placeholder="DD/MM/YYYY"
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Layout Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Layout Components
        </Typography>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Bento Grid Layout
          </Typography>
          <Typography variant="body1" paragraph>
            Dashboard-style grid layout for organizing content
          </Typography>
          
          <BentoBoxGrid rows={6} gap={2} column={8}>
            <BentoItem sx={{ gridArea: '1/1/3/5', bgcolor: 'primary.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Large Chart Area</Typography>
              <Typography variant="body2" color="white">Grid: 1/1/3/5 (2 rows, 4 columns)</Typography>
            </BentoItem>
            
            <BentoItem sx={{ gridArea: '1/5/2/7', bgcolor: 'secondary.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Metric 1</Typography>
              <Typography variant="body2" color="white">Grid: 1/5/2/7</Typography>
            </BentoItem>
            
            <BentoItem sx={{ gridArea: '1/7/2/9', bgcolor: 'success.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Metric 2</Typography>
              <Typography variant="body2" color="white">Grid: 1/7/2/9</Typography>
            </BentoItem>
            
            <BentoItem sx={{ gridArea: '2/5/4/9', bgcolor: 'error.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Widget Area</Typography>
              <Typography variant="body2" color="white">Grid: 2/5/4/9 (2 rows, 4 columns)</Typography>
            </BentoItem>
            
            <BentoItem sx={{ gridArea: '3/1/4/3', bgcolor: 'warning.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Small Widget</Typography>
              <Typography variant="body2" color="white">Grid: 3/1/4/3</Typography>
            </BentoItem>
            
            <BentoItem sx={{ gridArea: '3/3/4/5', bgcolor: 'info.light', p: 2, borderRadius: 2 }}>
              <Typography variant="h6" color="white">Small Widget</Typography>
              <Typography variant="body2" color="white">Grid: 3/3/4/5</Typography>
            </BentoItem>
          </BentoBoxGrid>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Navigation Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Navigation Components
        </Typography>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Custom Tab Component
          </Typography>
          
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Boxed Tabs</Typography>
            <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
              <CustomTab 
                label="Dashboard" 
                variant="primary" 
                type="boxed"
                tooltip="View dashboard overview"
                value={0}
              />
              <CustomTab 
                label="Analytics" 
                variant="primary" 
                type="boxed"
                tooltip="View analytics data"
                value={1}
              />
              <CustomTab 
                label="Settings" 
                variant="primary" 
                type="boxed"
                tooltip="Configure settings"
                value={2}
              />
            </Tabs>
          </Box>

          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Standard Tabs</Typography>
            <Tabs value={0}>
              <CustomTab 
                label="Overview" 
                variant="secondary" 
                type="standard"
                value={0}
              />
              <CustomTab 
                label="Details" 
                variant="secondary" 
                type="standard"
                value={1}
              />
              <CustomTab 
                label="History" 
                variant="secondary" 
                type="standard"
                value={2}
              />
            </Tabs>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Feedback Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Feedback Components
        </Typography>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Modal Component
          </Typography>
          <CustomButton
            variant="filled"
            color="primary"
            label="Open Modal"
            onClick={() => setModalOpen(true)}
          />
          
          <CustomModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Sample Modal"
            subtitle="This is a demonstration of the custom modal component"
            maxWidth="600px"
          >
            <Typography paragraph>
              This modal demonstrates the responsive behavior and styling of the CustomModal component.
              It includes a sticky header, scrollable content, and mobile-optimized animations.
            </Typography>
            <Typography paragraph>
              The modal automatically adapts to mobile devices with a slide-up animation and 
              full-screen width for better user experience.
            </Typography>
            <Box mt={3}>
              <Stack direction="row" spacing={2}>
                <CustomButton
                  variant="filled"
                  color="primary"
                  label="Confirm"
                  onClick={() => setModalOpen(false)}
                />
                <CustomButton
                  variant="outlined"
                  color="neutral"
                  label="Cancel"
                  onClick={() => setModalOpen(false)}
                />
              </Stack>
            </Box>
          </CustomModal>
        </Box>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Snackbar Component
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
            <CustomButton
              variant="filled"
              color="success"
              label="Show Success"
              onClick={() => {
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
              }}
            />
            <CustomButton
              variant="filled"
              color="error"
              label="Show Error"
              onClick={() => {
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }}
            />
            <CustomButton
              variant="filled"
              color="secondary"
              label="Show Warning"
              onClick={() => {
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
              }}
            />
          </Stack>

          <CustomSnackbar
            open={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
            message={`This is a ${snackbarSeverity} message!`}
            severity={snackbarSeverity}
            type="actionable"
            actionable={{
              label: "View Details",
              onClick: () => console.log('Action clicked')
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Data Display Components Section */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Data Display Components
        </Typography>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Status Chip Component
          </Typography>
          
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Outlined Style</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <StatusChip
                status={StatusEnums.Pending}
                label="Pending"
                type="outlined"
              />
              <StatusChip
                status={StatusEnums.Verified}
                label="Verified"
                type="outlined"
              />
              <StatusChip
                status={StatusEnums.Approved}
                label="Approved"
                type="outlined"
              />
              <StatusChip
                status={StatusEnums.Completed}
                label="Completed"
                type="outlined"
              />
              <StatusChip
                status={StatusEnums.Cancelled}
                label="Cancelled"
                type="outlined"
              />

              {/* <StateChip
                label="Example Chip"
                state={state}
                onClick={handleClick}
                onDelete={handleDelete}
                deleteIcon={ state === 'active' ? <Close sx={{ color: '#8C8C8C'}} /> : null }
              /> */}
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2} sx={{ my: 3 }}>
                <Box>
                  <Typography variant="h6">Multi-Select (With Delete)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {chipsData.map(chip => (
                      <StandardChip
                        key={chip.id}
                        label={chip.name}
                        selected={multiSelected.includes(chip.id)}
                        onChange={isSelected => toggleMulti(chip.id, isSelected)}
                        showDelete   // ✅ shows cross icon
                      />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6">Multi-Select (No Delete Icon)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {chipsData.map(chip => (
                      <StandardChip
                        key={chip.id}
                        label={chip.name}
                        selected={multiSelected.includes(chip.id)}
                        onChange={isSelected => toggleMulti(chip.id, isSelected)}
                      />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6">Single-Select (With Delete)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {chipsData.map(chip => (
                      <StandardChip
                        key={chip.id}
                        label={chip.name}
                        selected={singleSelected === chip.id}
                        onChange={isSelected =>
                          setSingleSelected(isSelected ? chip.id : null)
                        }
                        showDelete
                      />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6">Single-Select (No Delete Icon)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {chipsData.map(chip => (
                      <StandardChip
                        key={chip.id}
                        label={chip.name}
                        selected={singleSelected === chip.id}
                        onChange={isSelected =>
                          setSingleSelected(isSelected ? chip.id : null)
                        }
                      />
                    ))}
                  </Box>
                </Box>
            </Stack>
          </Box>

          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Filled Style</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <StatusChip
                status={StatusEnums.Pending}
                label="Pending"
                type="filled"
              />
              <StatusChip
                status={StatusEnums.Verified}
                label="Verified"
                type="filled"
              />
              <StatusChip
                status={StatusEnums.Approved}
                label="Approved"
                type="filled"
              />
              <StatusChip
                status={StatusEnums.Completed}
                label="Completed"
                type="filled"
              />
              <StatusChip
                status={StatusEnums.Cancelled}
                label="Cancelled"
                type="filled"
              />
            </Stack>
          </Box>

          <Box mb={3}>
            <Typography variant="h6" gutterBottom>Text Style</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
              <StatusChip
                status={StatusEnums.Success}
                label="Success"
                type="text"
              />
              <StatusChip
                status={StatusEnums.PartiallyCompleted}
                label="Partially Completed"
                type="text"
              />
              <StatusChip
                status={StatusEnums.NotVerified}
                label="Not Verified"
                type="text"
              />
            </Stack>
          </Box>
        </Box>

        <Box mb={4}>
          <Typography variant="h5" component="h3" gutterBottom>
            Use Case Examples
          </Typography>
          
          {/* Order Status Example */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Order Management</Typography>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography>Order #12345:</Typography>
              <StatusChip
                status={StatusEnums.Completed}
                label="Completed"
                type="filled"
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography>Order #12346:</Typography>
              <StatusChip
                status={StatusEnums.Pending}
                label="Pending Payment"
                type="outlined"
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography>Order #12347:</Typography>
              <StatusChip
                status={StatusEnums.Cancelled}
                label="Cancelled"
                type="text"
              />
            </Box>
          </Paper>

          {/* User Verification Example */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>User Verification</Typography>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography>John Doe:</Typography>
              <StatusChip
                status={StatusEnums.Verified}
                label="Verified"
                type="filled"
              />
              <Badge 
                badgeText="Premium" 
                badgeType="default" 
                badgePosition={{top: -8, right: -8}}
                badgeSize="small"
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography>Jane Smith:</Typography>
              <StatusChip
                status={StatusEnums.NotVerified}
                label="Pending Verification"
                type="outlined"
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Real-world Form Example */}
      <Box mb={6}>
        <Typography variant="h4" component="h2" gutterBottom>
          Real-world Example: Product Form
        </Typography>
        <Typography variant="body1" paragraph>
          Comprehensive form demonstrating multiple components working together
        </Typography>

        <Paper sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Product Name"
                variant="outlined"
                placeholder="Enter product name"
                required
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Category
              </Typography>
              <CustomSelect
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="">
                  <em>Select Category</em>
                </MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
                <MenuItem value="clothing">Clothing</MenuItem>
                <MenuItem value="books">Books</MenuItem>
              </CustomSelect>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box position="relative">
                <TextField
                  label="Price"
                  variant="outlined"
                  placeholder="0.00"
                  required
                  fullWidth
                />
                <Badge 
                  badgeText="Required" 
                  badgeType="default" 
                  badgePosition={{top: 0, right: 0}}
                  badgeSize="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Launch Date"
                  variant="outlined"
                  value={null}
                  onChange={() => {}}
                  helperText="Product launch date"
                  placeholder="DD/MM/YYYY"
                  disablePast
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                variant="outlined"
                placeholder="Enter product description"
                multiline
                rows={4}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                Product Status
              </Typography>
              <Stack direction="row" spacing={2}>
                <StatusChip
                  status={StatusEnums.Pending}
                  label="Draft"
                  type="outlined"
                />
                <StatusChip
                  status={StatusEnums.Approved}
                  label="Active"
                  type="filled"
                />
                <StatusChip
                  status={StatusEnums.Cancelled}
                  label="Inactive"
                  type="text"
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                Product Settings
              </Typography>
              <Stack spacing={2}>
                <ToggleSwitch
                  label="Featured Product"
                  checked={notificationToggle}
                  onChange={(e) => setNotificationToggle(e.target.checked)}
                  helperText="Display this product in featured section"
                />
                <ToggleSwitch
                  label="Enable Reviews"
                  checked={darkModeToggle}
                  onChange={(e) => setDarkModeToggle(e.target.checked)}
                  helperText="Allow customers to leave reviews"
                />
                <ToggleSwitch
                  label="In Stock"
                  checked={privacyToggle}
                  onChange={(e) => setPrivacyToggle(e.target.checked)}
                  helperText="Product is available for purchase"
                />
                <ToggleSwitch
                  label="Send Notifications"
                  size="small"
                  checked={toggleChecked}
                  onChange={(e) => setToggleChecked(e.target.checked)}
                  helperText="Notify customers about product updates"
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <CustomButton
                  variant="outlined"
                  color="neutral"
                  label="Cancel"
                  leftIcon={CancelIcon}
                />
                <CustomButton
                  variant="filled"
                  color="primary"
                  label="Save Product"
                  leftIcon={SaveIcon}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <Box>
        <StepperShowcase />
      </Box>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Checkbox, Radio Button & Toggle Switch Components
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Custom Checkbox
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6">Basic States</Typography>
                <CustomCheckbox 
                  label="Controlled checkbox" 
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <CustomCheckbox 
                  label="Indeterminate state" 
                  indeterminate={indeterminate}
                  onChange={() => setIndeterminate(!indeterminate)}
                />
                <CustomCheckbox label="Disabled unchecked" disabled />
                <CustomCheckbox label="Disabled checked" disabled checked />
                <CustomCheckbox label="Error state" error />
                
                {/* Different sizes */}
                <Typography variant="h6" sx={{ mt: 2 }}>Different Sizes</Typography>
                <CustomCheckbox label="Small" size="small" />
                <CustomCheckbox label="Medium (default)" size="medium" />
                <CustomCheckbox label="Large" size="large" />
                
                <Typography variant="h6" sx={{ mt: 2 }}>Label Placements</Typography>
                <CustomCheckbox label="Start placement" labelPlacement="start" />
                <CustomCheckbox label="End placement" labelPlacement="end" />
                <CustomCheckbox label="Top placement" labelPlacement="top" />
                <CustomCheckbox label="Bottom placement" labelPlacement="bottom" />
                
                {/* No label */}
                <Typography variant="h6" sx={{ mt: 2 }}>Without Label</Typography>
                <CustomCheckbox />
              </Box>
            </Paper>
          </Grid>
  
          {/* Radio Button Tests */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Custom Radio Button
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Basic radio group */}
                <CustomRadioGroup
                  label="Basic Radio Group"
                  options={['Option 1', 'Option 2', 'Option 3']}
                  value={radioValue}
                  onChange={(e) => setRadioValue(e.target.value)}
                  name="basic-radio"
                />
                
                <CustomRadioGroup
                  label="Plan Selection"
                  options={planOptions}
                  value={planValue}
                  onChange={(e) => setPlanValue(e.target.value)}
                  name="plan-radio"
                />
                
                <CustomRadioGroup
                  label="Orientation"
                  options={['Horizontal', 'Vertical']}
                  row={true}
                  value={orientationValue}
                  onChange={(e) => setOrientationValue(e.target.value)}
                  name="orientation-radio"
                />
                
                <CustomRadioGroup
                  label="Required Selection"
                  options={['Yes', 'No', 'Maybe']}
                  error={true}
                  helperText="Please select an option"
                  required
                  name="required-radio"
                />
                
                {/* Disabled */}
                <CustomRadioGroup
                  label="Disabled Group"
                  options={['Option A', 'Option B']}
                  disabled
                  name="disabled-radio"
                />
                
                {/* Single radio buttons */}
                <Typography variant="h6" sx={{ mt: 2 }}>Single Radio Buttons</Typography>
                <CustomRadio label="Single radio 1" value="single1" name="single-group" />
                <CustomRadio label="Single radio 2" value="single2" name="single-group" />
                <CustomRadio label="Disabled radio" disabled value="disabled" name="single-group" />
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Box>
      <Box sx={{}}>
        <Typography variant="h4" component="h1" gutterBottom> Toggle Switch</Typography>
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Toggle Switch
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6">Basic States</Typography>
              <ToggleSwitch 
                label="Controlled toggle" 
                checked={toggleChecked}
                onChange={(e) => setToggleChecked(e.target.checked)}
                helperText="Receive email notifications"
              />
              <ToggleSwitch label="Disabled unchecked" disabled />
              <ToggleSwitch label="Disabled checked" disabled checked />
              
              {/* Different sizes */}
              <Typography variant="h6" sx={{ mt: 2 }}>Different Sizes</Typography>
              <ToggleSwitch 
                label="Small toggle" 
                size="small"
                checked={darkModeToggle}
                onChange={(e) => setDarkModeToggle(e.target.checked)}
              />
              <ToggleSwitch 
                label="Medium toggle (default)" 
                size="medium"
                checked={privacyToggle}
                onChange={(e) => setPrivacyToggle(e.target.checked)}
              />
              <ToggleSwitch 
                label="Large toggle" 
                size="large"
                checked={toggleChecked}
                onChange={(e) => setToggleChecked(e.target.checked)}
              />
            </Box>
          </Paper>
        </Box>
      </Box>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ my: 2}}> Paper Elevation</Typography>
        <PaperElevation elevation={1} sx={{ my: 2 }}>Elevation 1</PaperElevation>
        <PaperElevation elevation={2} sx={{ mb: 2 }}>Elevation 2</PaperElevation>
        <PaperElevation elevation={3} sx={{ mb: 2 }}>Elevation 3</PaperElevation>
        <PaperElevation elevation={4} sx={{ my: 2 }}>Elevation 4</PaperElevation>
      </Box>
        <Box sx={{ background: '#fff', padding: 2, my: 2}}>
          <Typography variant="h2" component="h2" gutterBottom>
            Avatar Component
          </Typography>
          <Avatar label="Manorma" bg="#1976d2" />

          <Box sx={{ mt: 2 }}>
            <Avatar
              label="John Doe"
              src="https://i.pravatar.cc/150?img=32"
              size={40}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Avatar
              label="John Doe"
              icon={<Person />}
              bg="#F7F7F7"
              size={40}
            />
          </Box>
          <Avatar src="https://i.pravatar.cc/150?img=12" />
        </Box>
        <Box>
          <CustomTooltip title="Tooltip on top" placement="top" variant='main' size='small'>
            <Button>Top</Button>
          </CustomTooltip>

          <CustomTooltip title="Tooltip on right" placement="right" variant='main'>
            <Button>Right</Button>
          </CustomTooltip>

          <CustomTooltip title="Tooltip on bottom" placement="bottom" >
            <Button>Bottom</Button>
          </CustomTooltip>

          <CustomTooltip title="Tooltip on left" placement="left">
            <Button>Left</Button>
          </CustomTooltip>

          <CustomTooltip title="Tooltip on top" placement="top" showArrow={false}>
            <Button>without Arrow</Button>
          </CustomTooltip>
        </Box>
        <Box sx={{ my: 4 }}>
          <Typography variant="display" gutterBottom sx={{ my: 2}}> Illustration Example</Typography>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <IllustrationWrapper 
              illustration={<NoInventorySvg />}
              title="Custom Title"
              subtitle="This is a custom subtitle with IllustrationWrapper"
            />
            <IllustrationWrapper 
              illustration={<EmptyCartSvg />}
              title="Left Aligned"
              subtitle="This illustration is left aligned"
              alignment="left"
            />
            <IllustrationWrapper 
              illustration={<DeliveryTruckSvg />}
              title="Custom Title"
              subtitle="This is a custom subtitle with IllustrationWrapper"
            />
            <IllustrationWrapper 
              illustration={<NoDataFoundSvg />}
              title="Custom Title"
              subtitle="This is a custom subtitle with IllustrationWrapper"
            />
          </Box>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default ComponentShowcase;
